import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useEffect, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button, Tap } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import TextArea from '@/components/ui/TextArea';

import { ArgRow } from './arg-row';
import { Callout } from './callout';
import { DeviceNoteCallout } from './device-note-callout';
import { DialogHeader } from './dialog-header';
import { EditableList } from './editable-list';
import { JsonFlagsNotice } from './json-flags-notice';
import { JsonPastePanel } from './json-paste-panel';
import { KeyValueRow } from './key-value-row';
import { ModalCard } from './modal-card';
import { blurSwapVariants, SWAP_TRANSITION } from './motion';
import { NameSlugField } from './name-slug-field';
import { SourceField } from './source-field';
import { StepSection } from './step-section';
import { TestConnection } from './test-connection';

import type { AddServerForm } from './use-add-server-form';

const SKIPPED_STEP_TIP =
  'This server ships inside Littlebird — its location and settings are fixed, so there is nothing to fill in.';

type StepId = 0 | 1 | 2 | 3;

export function AddServerModal({
  form,
  onBack,
  onCancel,
  initialStep = 1,
}: {
  form: AddServerForm;
  onBack?: () => void;
  onCancel?: () => void;
  initialStep?: StepId;
}) {
  const [openStep, setOpenStep] = useState<StepId>(initialStep);
  const toggleStep = (n: StepId) => setOpenStep((o) => (o === n ? 0 : n));
  const [setupDismissed, setSetupDismissed] = useState(false);

  const reduce = useReducedMotion();
  const swapTransition = reduce ? { duration: 0 } : SWAP_TRANSITION;

  const hasRequirements = form.requirements.length > 0;
  const hasUnmetRequirements = form.unmetRequirements.length > 0;

  useEffect(() => {
    if (hasUnmetRequirements) setOpenStep(2);
    else if (form.test === 'success') setOpenStep(3);
  }, [form.test, hasUnmetRequirements]);
  useEffect(() => {
    if (!form.jsonOpen) setOpenStep(1);
  }, [form.jsonOpen]);

  const isBundled = form.transport === 'bundled';
  const primaryValue =
    form.transport === 'http'
      ? form.url
      : form.transport === 'binary'
        ? form.command
        : form.pkg;
  const step1Complete = isBundled || primaryValue.trim() !== '';
  const isUrl = form.transport === 'http';

  const flaggedEnvKeys = new Set(
    form.jsonFlags
      .filter((f) => f.startsWith('placeholder-env:'))
      .map((f) => f.slice('placeholder-env:'.length))
  );
  const flaggedHeaderKeys = new Set(
    form.jsonFlags
      .filter((f) => f.startsWith('placeholder-header:'))
      .map((f) => f.slice('placeholder-header:'.length))
  );

  const argsUnmet = form.unmetRequirements.some((r) => r.type === 'min_args');
  const unmetEnvNames = new Set(
    form.unmetRequirements.flatMap((r) =>
      r.type === 'env' && r.name ? [r.name.toLowerCase()] : []
    )
  );
  const unmetHeaderNames = new Set(
    form.unmetRequirements.flatMap((r) =>
      r.type === 'header' && r.name ? [r.name.toLowerCase()] : []
    )
  );
  const envPlaceholders = new Map(
    form.envHints.map((hint) => [
      hint.name.trim().toLowerCase(),
      hint.placeholder,
    ])
  );

  const argsList = (
    <EditableList
      label="Arguments"
      addLabel="Add an argument"
      onAdd={form.addArg}
    >
      {form.args.map((value, i) => (
        <ArgRow
          key={i}
          value={value}
          onChange={(v) => form.setArg(i, v)}
          onPickFolder={() => form.pickFolder(i)}
          onRemove={() => form.removeArg(i)}
          placeholder={form.argHints[i]?.label}
          danger={argsUnmet && value.trim() === ''}
        />
      ))}
    </EditableList>
  );
  const envList = (
    <EditableList
      label="Environment variables"
      addLabel="Add an environment variable"
      onAdd={form.addEnv}
    >
      {form.envs.map((row, i) => (
        <KeyValueRow
          key={i}
          keyValue={row.key}
          value={row.value}
          onKeyChange={(v) => form.setEnvField(i, 'key', v)}
          onValueChange={(v) => form.setEnvField(i, 'value', v)}
          onRemove={() => form.removeEnv(i)}
          keyPlaceholder="KEY"
          valuePlaceholder={
            envPlaceholders.get(row.key.trim().toLowerCase()) ?? 'value'
          }
          keyWidthClass="w-[38%]"
          warn={flaggedEnvKeys.has(row.key)}
          danger={unmetEnvNames.has(row.key.trim().toLowerCase())}
          secret={{
            reveal: row.reveal,
            onToggle: () => form.toggleEnvReveal(i),
          }}
        />
      ))}
    </EditableList>
  );
  const headersList = (
    <EditableList
      label="Headers"
      addLabel="Add a header"
      onAdd={form.addHeader}
    >
      {form.headers.map((row, i) => (
        <KeyValueRow
          key={i}
          keyValue={row.key}
          value={row.value}
          onKeyChange={(v) => form.setHeaderField(i, 'key', v)}
          onValueChange={(v) => form.setHeaderField(i, 'value', v)}
          onRemove={() => form.removeHeader(i)}
          keyPlaceholder="Authorization"
          valuePlaceholder="value"
          keyWidthClass="w-[40%]"
          warn={flaggedHeaderKeys.has(row.key)}
          danger={unmetHeaderNames.has(row.key.trim().toLowerCase())}
        />
      ))}
    </EditableList>
  );

  const subtitle = form.jsonOpen ? (
    <>
      Paste the server&apos;s <span className="font-mono">mcpServers</span>{' '}
      block — we&apos;ll detect the runtime, package, and name.
    </>
  ) : isBundled ? (
    <span className="block">
      This server ships with the app — test the connection, then name it.
    </span>
  ) : (
    <>
      <span className="block">
        Register an MCP server running on this device.
      </span>
      <span className="block">
        Already have a config?{' '}
        <button
          type="button"
          onClick={form.toggleJson}
          className="font-medium text-foreground underline underline-offset-2 hover:text-foreground-subtle"
        >
          Paste config
        </button>{' '}
        to skip ahead.
      </span>
    </>
  );

  const addLabel =
    form.test === 'success' && form.name.trim()
      ? `Add ${form.name.trim()}`
      : 'Add server';

  const jsonDirty = form.jsonText.trim() !== '';

  const footer = (
    <XStack className="items-center justify-end gap-2 border-t border-border p-4 px-6">
      {form.jsonOpen ? (
        <Tap disabled={!jsonDirty}>
          <Button
            variant="default"
            size="sm"
            disabled={!jsonDirty}
            onClick={() => form.applyJson()}
          >
            Review details
            <Icons.arrowRight className="size-4" />
          </Button>
        </Tap>
      ) : (
        <>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Tap disabled={!form.canSave}>
            <Button
              variant="default"
              size="sm"
              disabled={!form.canSave}
              isLoading={form.saving}
              onClick={form.save}
            >
              {addLabel}
            </Button>
          </Tap>
        </>
      )}
    </XStack>
  );

  return (
    <ModalCard onClose={onCancel} className="max-h-[85vh] overflow-y-auto">
      <DialogHeader
        onBack={form.jsonOpen ? form.toggleJson : onBack}
        icon={<Icons.plugZap className="size-5" />}
        title="Add custom MCP server"
        subtitle={subtitle}
      />
      <YStack className="gap-3 p-6 pt-4">
        <AnimatePresence mode="wait" initial={false}>
          {form.jsonOpen ? (
            <motion.div
              key="paste"
              variants={blurSwapVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={swapTransition}
            >
              <JsonPastePanel
                text={form.jsonText}
                error={form.jsonError}
                choices={form.jsonChoices}
                preview={form.jsonPreview}
                onTextChange={form.setJsonText}
                onPick={(choice) => form.applyJson(choice)}
              />
            </motion.div>
          ) : (
            <motion.div
              key="form"
              variants={blurSwapVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={swapTransition}
              className="flex flex-col gap-3"
            >
              {form.deviceNote?.length ? (
                <DeviceNoteCallout lines={form.deviceNote} />
              ) : null}

              <StepSection
                index={1}
                title="Where's the server?"
                pill={isBundled ? 'skipped' : undefined}
                pillTip={SKIPPED_STEP_TIP}
                locked={isBundled}
                lockedTip={SKIPPED_STEP_TIP}
                open={openStep === 1}
                onToggle={() => toggleStep(1)}
              >
                <SourceField
                  source={form.source}
                  onSourceChange={form.changeSource}
                  pkg={form.pkg}
                  onPkgChange={form.changePkg}
                  command={form.command}
                  onCommandChange={form.changeCommand}
                  onPickFile={form.pickCommandFile}
                  url={form.url}
                  onUrlChange={form.changeUrl}
                  urlError={form.urlError}
                />
              </StepSection>

              <StepSection
                index={2}
                title="Extra settings"
                pill={
                  isBundled && !hasRequirements
                    ? 'skipped'
                    : isUrl
                      ? 'for URLs'
                      : form.transport === 'binary'
                        ? 'for programs'
                        : 'for packages'
                }
                pillTip={
                  isBundled && !hasRequirements ? SKIPPED_STEP_TIP : undefined
                }
                locked={
                  (isBundled && !hasRequirements) ||
                  (!isBundled && !step1Complete)
                }
                lockedTip={
                  isBundled
                    ? SKIPPED_STEP_TIP
                    : 'Fill in step 1 first to open this'
                }
                open={openStep === 2}
                onToggle={() => toggleStep(2)}
              >
                <YStack className="gap-3">
                  {form.setupInstructions.length > 0 && !setupDismissed && (
                    <Callout
                      tone="neutral"
                      icon={<Icons.info className="size-4" />}
                      onDismiss={() => setSetupDismissed(true)}
                    >
                      <YStack className="min-w-0 gap-0.5">
                        <span className="text-sm font-medium text-foreground">
                          Before you connect
                        </span>
                        <ol className="list-decimal pl-4 text-xs leading-relaxed text-foreground-muted">
                          {form.setupInstructions.map((step, i) => (
                            <li key={i}>{step}</li>
                          ))}
                        </ol>
                      </YStack>
                    </Callout>
                  )}
                  {hasUnmetRequirements && (
                    <Callout
                      tone="danger"
                      icon={<Icons.warn className="size-4" />}
                    >
                      <YStack className="min-w-0 gap-0.5">
                        <span className="text-sm font-medium text-foreground">
                          This server needs more setup
                        </span>
                        {form.unmetRequirements.map((requirement) => (
                          <p
                            key={requirement.message}
                            className="text-xs leading-relaxed text-foreground-muted"
                          >
                            {requirement.message}
                          </p>
                        ))}
                      </YStack>
                    </Callout>
                  )}
                  {isUrl ? (
                    headersList
                  ) : (
                    <YStack className="gap-3">
                      {argsList}
                      {envList}
                      <p className="text-[10px] text-foreground-muted">
                        Only if the package&apos;s README asks for them.
                      </p>
                    </YStack>
                  )}
                </YStack>
              </StepSection>

              <TestConnection
                test={form.test}
                canTest={form.canTest}
                serverName={form.testServerName}
                toolCount={form.testTools}
                errorText={form.testError}
                missingPermissions={form.missingPermissions}
                onTest={form.testConnection}
              />

              {form.duplicateName && (
                <Callout tone="danger" icon={<Icons.warn className="size-4" />}>
                  <YStack className="min-w-0 gap-0.5">
                    <span className="text-sm font-medium text-foreground">
                      You already have this server
                    </span>
                    <p className="text-xs leading-relaxed text-foreground-muted">
                      Your{' '}
                      <b className="font-medium text-foreground">
                        {form.duplicateName}
                      </b>{' '}
                      server is already running with this exact setup. You can
                      run more than one copy. Just give this one a different
                      config in{' '}
                      <button
                        type="button"
                        onClick={() => setOpenStep(2)}
                        className="font-medium text-foreground underline underline-offset-2 hover:text-foreground-subtle"
                      >
                        Extra settings
                      </button>
                      .
                    </p>
                  </YStack>
                </Callout>
              )}

              <StepSection
                index={3}
                title="Name & description"
                locked={
                  form.test !== 'success' ||
                  !!form.duplicateName ||
                  hasUnmetRequirements
                }
                lockedTip={
                  hasUnmetRequirements
                    ? 'Finish the setup in Extra settings first'
                    : form.duplicateName
                      ? 'This server is already added. Give it a different config in Extra settings'
                      : 'Test the connection first to open this'
                }
                open={openStep === 3}
                onToggle={() => toggleStep(3)}
              >
                <YStack className="gap-3">
                  <NameSlugField
                    name={form.name}
                    slug={form.slug}
                    onNameChange={form.setName}
                  />
                  <YStack className="gap-1.5">
                    <XStack className="items-center justify-between">
                      <label className="text-xs text-foreground-subtle">
                        Description
                      </label>
                      <span className="font-mono text-[10px] text-foreground-muted">
                        {form.description.length}/256
                      </span>
                    </XStack>
                    <TextArea
                      value={form.description}
                      onChange={(e) => form.setDescription(e.target.value)}
                      maxLength={256}
                      spellCheck={false}
                      className="min-h-[64px] resize-y text-sm"
                    />
                  </YStack>
                  <p className="text-[10px] text-foreground-muted">
                    Detected from the server — edit either field before saving.
                  </p>
                </YStack>
              </StepSection>

              {form.jsonFlags.length > 0 && (
                <JsonFlagsNotice flags={form.jsonFlags} />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </YStack>
      {footer}
    </ModalCard>
  );
}
